import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import FecthingPage from "../../CommonComponents/FecthingPage";
import { creditAndDebitAmount } from "../../../../../redux/features/Settings/Statistics";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";

const DebitCreditAmount = () => {
  // default values for the graph stored in variables
  const defaultGraphData = {
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [200, 500, 300, 100, 600, 800, 350],
    dataSecond: [100, 700, 200, 300, 500, 400, 250],
    xAxisData: ["01", "02", "03", "04", "05", "06", "07"],
    creditAmount: 1001222000,
    debitAmount: 40122200000,
  };
  const [graphData, setGraphData] = useState(defaultGraphData);

  const [selectedDays, setSelectedDays] = useState("last 7 days");
  const [selectedAmount, setSelectedAmount] = useState("");
  const [fetchPageEnabled, setFetchPageEnabled] = useState(true);
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);

  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.refreshToken.data);
  const { creditAndDebitAmountLoading, creditAndDebitAmountError } =
    useSelector((state) => state.settingStatistics);

  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /* Helper to transform API response into graph-friendly arrays */
  const transformReportData = (reports, selectedAmount) => {
    return reports.reduce(
      (acc, val, index) => {
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(
          selectedAmount
            ? formatTotalBalance(val.credit, selectedAmount).amount
            : val.credit,
        );
        acc.graphDataSecond.push(
          selectedAmount
            ? formatTotalBalance(val.debit, selectedAmount).amount
            : val.debit,
        );
        acc.xAxisLabels.push(val.day);
        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
      },
    );
  };

  const handleCalculation = useCallback(
    (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        setRawReportData(reportData);

        const {
          defaultGraphDataItemsForXaxis,
          graphDataFirst,
          graphDataSecond,
          xAxisLabels,
        } = transformReportData(
          reportData.generate_reports || [],
          selectedAmount,
        );

        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          dataSecond: graphDataSecond,
          xAxisData: xAxisLabels,
          creditAmount: reportData.total_credit_amount,
          debitAmount: reportData.total_debit_amount,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: "Fetched successfully.",
            icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
      } else {
        // fallback to defaults
        setGraphData(defaultGraphData);
        setFetchPageEnabled(response?.payload?.status !== 404);
        toastShownRef.current = false;
        setIsError(true);
      }
    },
    [selectedDays, entityId],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    if (!entityId) return;
    setIsError(false);

    const response = await dispatch(
      creditAndDebitAmount({ entity_id: entityId, period: selectedDays }),
    );
    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /* Button handler */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /*Auto-fetch on dependency change */
  useEffect(() => {
    if (!fetchPageEnabled) {
      fetchData();
    }
  }, [fetchPageEnabled, selectedDays]);

  /* Recalculate graph when only unit changes */
  useEffect(() => {
    if (rawReportData) {
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
      } = transformReportData(
        rawReportData.generate_reports || [],
        selectedAmount,
      );

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        creditAmount: rawReportData.total_credit_amount,
        debitAmount: rawReportData.total_debit_amount,
      });
    }
  }, [selectedAmount, rawReportData]);

  return (
    <div className="w-width-full flex flex-col bg-primary-card rounded-border-radius-md">
      <GraphHeader
        handlingOptionsRequired={[
          { type: "days", setterState: setSelectedDays },
          { type: "amounts", setterState: setSelectedAmount },
        ]}
        titleText={"Debit and credit amount"}
      />
      <div className="flex flex-col relative">
        <FecthingPage
          fetchPageEnabled={fetchPageEnabled}
          loading={creditAndDebitAmountLoading || fetchPageLoading}
          isError={isError}
          errorData={creditAndDebitAmountError}
          handleFunction={handleFetchClick}
        />
        <div className="w-width-full">
          <div>
            <CommonLineGraph
              graphDataFirst={graphData.dataFirst}
              graphDataSecond={graphData.dataSecond}
              xAxisLabels={graphData.xAxisData}
              graphDataFirstColor={"var(--settled)"}
              graphDataSecondColor={"var(--queued)"}
              defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
              amountFormatForLegend={selectedAmount}
            />
          </div>
        </div>
        <div className="w-width-full bg-primary-nav rounded-b-border-radius-md">
          <div className="flex Qsmd:flex-row flex-col Qsmd:gap-gap-md gap-gap-sm py-padding-sm px-padding-md">
            <div className="flex gap-gap-md items-center">
              <div className="text-text-secondary text-font-xxs">
                Credit amount :
              </div>
              <div className="text-positive-act text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.creditAmount,
                      selectedAmount === "" ? "amount" : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>
            <div className="flex gap-gap-md items-center Qsmd:border-l-border Qsmd:border-t-border-base border-t-border Qsmd:border-l-primary-bg border-t-primary-bg Qsmd:pl-padding-md Qsmd:pt-padding-base pt-padding-sm">
              <div className="text-text-secondary text-font-xxs">
                Debit amount :
              </div>
              <div className="text-queued text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.debitAmount,
                      selectedAmount === "" ? "amount" : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebitCreditAmount;
